import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { _Button } from "./button";
import { useTiktokService } from "@/hooks/tiktok.hooks";
import { useState } from "react";
import { AlertCircle, ExternalLinkIcon } from "lucide-react";
import { useToast } from "@/hooks/toast.hooks";

export const AuthorizationExpiredModal = ({
  open,
  setOpen,
  trigger,
  onSuccess,
}: {
  trigger: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { buildAuthorizeUrl } = useTiktokService();

  const handleReauthorize = async () => {
    setLoading(true);
    try {
      const response = await buildAuthorizeUrl({ isUS: false });
      if (!response.success) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate TikTok authorization URL",
        });
        return;
      }
      window.location.href = response.content.authorizeUrl;
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred while generating authorization URL",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <AlertDialogTitle>Authorization Expired</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-lg space-y-4">
            <p>
              Your TikTok Shop seller account authorization has expired. To continue using our services, 
              you'll need to reauthorize your account.
            </p>
            <div className="bg-muted p-4 rounded-lg">
              <p className="font-medium text-base">Pro Tip:</p>
              <p>
                To prevent future interruptions, make sure to set your authorization to 
                "indefinite/unlimited" during the reauthorization process.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="pt-6">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <_Button
            onClick={handleReauthorize}
            loading={loading}
            className="h-10 bg-green-500 hover:bg-green-600 group flex items-center justify-between px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150 ease-in-out"
          >
            Reauthorize with TikTok
            <ExternalLinkIcon className="ml-2 opacity-75 group-hover:opacity-100 h-5 w-5" aria-hidden="true" />
          </_Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
